import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDataEntryContext } from "../context/reducer.provider";
import { useTimerContext } from "../context/timer.provider";
import { Process, ProcessResponse, Torch } from "../models/torches.models";
import {
  changeCasterHeatStatus,
  deleteTorchData,
  getTorchesProcess,
  upsertHeat,
  upsertProcessData,
  upsertTorchData,
  upsertTorchesData,
} from "../repositories/torches.repositories";

export const useTorchesDataEntryActions = () => {
  const navigate = useNavigate();

  const {
    state,
    actions: {
      setDEProcess,
      setErrorMessage,
      setIsDataLoading,
      setIsSubmitLoading,
      setNotificationMessage,
    },
  } = useDataEntryContext();

  const { restart } = useTimerContext();

  const loadData = async (processId: number | null) => {
    setIsDataLoading(true);
    const resp = await getTorchesProcess({ ProcessId: processId });
    if (resp.ok) {
      const data = resp.data as ProcessResponse;
      const process = data.tables["0"].rows[0] as Process;
      setDEProcess({
        process,
        torches: data.tables["1"].rows,
        groupedLength: data.tables["2"].rows,
        groupedBilletsYard: data.tables["3"].rows,
      });
      navigate(`/caster-torches/${process.ProcessId}`);
    } else {
      setErrorMessage(resp.message!);
    }
    setIsDataLoading(false);
  };

  const saveProcessData = async ({
    processId,
    field,
    value,
  }: {
    processId: number;
    field: string;
    value: boolean | string | number | Date | null;
  }) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await upsertProcessData({
      processId,
      field,
      value,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Process saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const saveHeat = async (field: string, value: any) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await upsertHeat({
      HeatId: state.deProcess?.process!.HeatId!,
      field: field,
      value: value,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Process saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const refreshData = useCallback(async () => {
    if (state.deProcess?.process?.ProcessId) {
      loadData(state.deProcess.process.ProcessId).then(() => {
        restart();
      });
    } else {
      loadData(null).then(() => {
        restart();
      });
    }
  }, [state.deProcess?.process?.ProcessId]);

  const saveTorch = async (torch: Torch) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await upsertTorchData({
      ProcessId: state.deProcess?.process!.ProcessId!,
      Strand: torch.Strand,
      ColdLength: torch.ColdLength,
      HotLength: torch.HotLength,
      WeightRounded: torch.WeightRounded,
      WeightOriginal: torch.WeightOriginal,
      Undefined: torch.Undefined,
      Scrapped: torch.Scrapped,
      Billets: torch.Billets,
      TotalCuts: torch.TotalCuts,
      IsManual: true,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Torch saved successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const saveTorches = async (processId: number, torches: Torch[]) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await upsertTorchesData({
      torches: torches.map((torch) => ({
        ProcessId: processId,
        ...torch,
        IsManual: true,
      })),
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Torch saved successfully");
      await refreshData();
      return true;
    } else {
      setErrorMessage(resp.message!);
      return false;
    }
  };

  const deleteTorch = async (torch: Torch) => {
    setIsSubmitLoading(true);
    setErrorMessage("");
    const resp = await deleteTorchData({
      ProcessId: state.deProcess?.process!.ProcessId!,
      Strand: torch.Strand,
      HotLength: torch.HotLength,
    });
    setIsSubmitLoading(false);
    if (resp.ok) {
      setNotificationMessage("Torch deleted successfully");
      await refreshData();
    } else {
      setErrorMessage(resp.message!);
    }
  };

  const changeHeatStatus = async (heatId: number, status: string) => {
    setIsSubmitLoading(true);
    const resp = await changeCasterHeatStatus({
      heatId: heatId,
      status: status,
    });
    if (resp.ok) {
      const data = resp.data;
      loadData(data[0].ProcessId);
      await refreshData();
      return data;
    } else {
      setErrorMessage(resp.message!);
    }
    setIsSubmitLoading(false);
  };

  return {
    loadData,
    saveProcessData,
    saveHeat,
    saveTorch,
    saveTorches,
    deleteTorch,
    refreshData,
    changeHeatStatus,
  };
};
